// Supabase database utility for storing 3D model metadata and asset URLs
import { supabase, TABLES, STORAGE_BUCKETS } from '../lib/supabase.js';
import { upload3DModelAssets, delete3DModelAssets } from './supabaseStorage.js';
import { getOrCreateCreatorProfile } from './profileManagement.js';
import { safeFetchSingle, safeUpdate, safeInsert } from './supabaseErrorHandler.js';
import { asNumericId, isMintAddress, applyPieceFilter } from './pieceIdHelpers.js';

/**
 * Get or create a creator by wallet address
 * @param {string} walletAddress - The wallet address
 * @returns {Promise<Object>} - The creator object
 */
export const getOrCreateCreator = async (walletAddress) => {
  try {
    // Validate wallet address
    if (!walletAddress || typeof walletAddress !== 'string') {
      throw new Error('Invalid wallet address provided');
    }

    console.log('Getting or creating creator for wallet:', walletAddress);

    // Use the full profile creation function to ensure all fields are included
    const result = await getOrCreateCreatorProfile(walletAddress);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to get or create creator profile');
    }
    
    console.log('Got/created creator with full profile:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error in getOrCreateCreator:', error);
    throw error;
  }
};

/**
 * Save piece metadata to Supabase database
 * @param {Object} pieceData - Piece metadata and asset URLs
 * @returns {Promise<Object>} - The saved piece with ID
 */
export const savePieceToDatabase = async (pieceData) => {
  try {
    const { data, error } = await supabase
      .from(TABLES.PIECES)
      .insert(pieceData)
      .select()
      .maybeSingle();

    if (error) {
      console.error('❌ Error saving piece to database:', error);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      console.error('Error hint:', error.hint);
      console.error('Piece data that failed:', JSON.stringify(pieceData, null, 2));
      throw error;
    }

    if (!data) {
      console.error('❌ No data returned after inserting piece');
      throw new Error('Failed to save piece - no data returned');
    }

    console.log('Piece saved to database:', data);
    return data;
  } catch (error) {
    console.error('Error in savePieceToDatabase:', error);
    throw error;
  }
};

/**
 * Get piece by ID or mint address
 * @param {number|string|Object} identifier - Piece ID (number), mint address (string), or { pieceId, mintAddress }
 * @returns {Promise<Object|null>} - The piece or null if not found
 */
export const getPieceById = async (identifier) => {
  try {
    // Normalize input
    let pieceId, mintAddress;
    
    if (typeof identifier === 'object' && identifier !== null) {
      pieceId = identifier.pieceId;
      mintAddress = identifier.mintAddress;
    } else {
      const numericId = asNumericId(identifier);
      if (numericId !== null) {
        pieceId = numericId;
      } else if (isMintAddress(identifier)) {
        mintAddress = identifier;
      } else {
        pieceId = identifier; // Fall back
      }
    }
    
    let query = supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `);
    
    query = applyPieceFilter(query, { pieceId, mintAddress, isWrite: false });
    
    const { data, error } = await query.maybeSingle();

    if (error) {
      const displayId = mintAddress ? `mint=${mintAddress.slice(0, 8)}...` : `id=${pieceId}`;
      console.error('❌ Error fetching piece:', error, { pieceId, mintAddress });
      return {
        success: false,
        message: `Failed to fetch piece: ${error.message}`,
        code: error.code || 'FETCH_ERROR',
      };
    }

    if (!data) {
      const displayId = mintAddress ? `mint=${mintAddress.slice(0, 8)}...` : `id=${pieceId}`;
      console.log(`ℹ️ Piece not found: ${displayId}`);
      return {
        success: false,
        message: `Piece not found for provided ${mintAddress ? 'mint address' : 'id'}`,
        code: 'NOT_FOUND',
      };
    }

    // Add holder information if holder_wallet exists
    if (data.holder_wallet) {
      try {
        const { data: holderData } = await supabase
          .from('creators')
          .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, banner_url, bio, social_links, total_pieces_created')
          .eq('wallet', data.holder_wallet)
          .maybeSingle();
        
        return {
          ...data,
          holder: holderData
        };
      } catch (error) {
        console.log('No holder profile found for wallet:', data.holder_wallet);
        return data;
      }
    }

    return data;
  } catch (error) {
    console.error('Error getting piece by ID:', error);
    throw error;
  }
};

/**
 * Get all public pieces with pagination (excluding drafts)
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} - Object with pieces and pagination info
 */
export const getAllPieces = async (page = 0, limit = 20) => {
  try {
    console.log('🔍 getAllPieces: Starting query with page:', page, 'limit:', limit);
    const from = page * limit;
    const to = from + limit - 1;

    console.log('🔍 getAllPieces: Query range from:', from, 'to:', to);

    const { data, error, count } = await supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('listing_status', 'listed')  // Only listed pieces
      .order('created_at', { ascending: false })
      .range(from, to);

    console.log('🔍 getAllPieces: Supabase response - data:', data, 'error:', error, 'count:', count);

    if (error) {
      console.error('❌ getAllPieces: Error getting all pieces:', error);
      throw error;
    }

    // Add holder information to pieces
    const piecesWithHolders = await Promise.all((data || []).map(async (piece) => {
      if (piece.holder_wallet) {
        try {
          const { data: holderData } = await supabase
            .from('creators')
            .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, banner_url, bio, social_links, total_pieces_created')
            .eq('wallet', piece.holder_wallet)
            .maybeSingle();
          
          return {
            ...piece,
            holder: holderData
          };
        } catch (error) {
          console.log('No holder profile found for wallet:', piece.holder_wallet);
          return piece;
        }
      }
      return piece;
    }));

    const result = {
      pieces: piecesWithHolders,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };

    console.log('🔍 getAllPieces: Returning result:', result);
    return result;
  } catch (error) {
    console.error('❌ getAllPieces: Error in getAllPieces:', error);
    throw error;
  }
};

/**
 * Get pieces by category
 * @param {string} category - Piece category
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} - Object with pieces and pagination info
 */
export const getPiecesByCategory = async (category, page = 0, limit = 20) => {
  try {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('category', category)
      .eq('listing_status', 'listed')  // Only listed pieces
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error getting pieces by category:', error);
      throw error;
    }

    // Add holder information to pieces
    const piecesWithHolders = await Promise.all((data || []).map(async (piece) => {
      if (piece.holder_wallet) {
        try {
          const { data: holderData } = await supabase
            .from('creators')
            .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, banner_url, bio, social_links, total_pieces_created')
            .eq('wallet', piece.holder_wallet)
            .maybeSingle();
          
          return {
            ...piece,
            holder: holderData
          };
        } catch (error) {
          console.log('No holder profile found for wallet:', piece.holder_wallet);
          return piece;
        }
      }
      return piece;
    }));

    return {
      pieces: piecesWithHolders,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Error in getPiecesByCategory:', error);
    throw error;
  }
};

/**
 * Update piece metadata
 * @param {number|string|Object} identifier - Piece ID (number), mint address (string), or { pieceId, mintAddress }
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object|null>} - Updated piece or null if not found
 */
export const updatePiece = async (identifier, updates) => {
  try {
    // Normalize input
    let pieceId, mintAddress;
    
    if (typeof identifier === 'object' && identifier !== null && !Array.isArray(identifier)) {
      // Check if this is updates object (legacy) or identifier object
      if (identifier.pieceId !== undefined || identifier.mintAddress !== undefined) {
        pieceId = identifier.pieceId;
        mintAddress = identifier.mintAddress;
      } else {
        // This is the updates object being passed as first param (legacy single-param call)
        console.error('❌ updatePiece called with updates as first param - needs identifier');
        throw new Error('updatePiece requires identifier as first parameter');
      }
    } else {
      const numericId = asNumericId(identifier);
      if (numericId !== null) {
        pieceId = numericId;
      } else if (isMintAddress(identifier)) {
        mintAddress = identifier;
      } else {
        pieceId = identifier; // Fall back
      }
    }
    
    let query = supabase
      .from(TABLES.PIECES)
      .update(updates);
    
    query = applyPieceFilter(query, { pieceId, mintAddress, isWrite: true });
    
    const { data, error } = await query
      .select()
      .maybeSingle();

    if (error) {
      console.error('❌ Error updating piece:', error);
      return {
        success: false,
        message: `Failed to update piece: ${error.message}`,
        code: error.code || 'UPDATE_ERROR',
      };
    }
    
    if (!data) {
      const displayId = mintAddress ? `mint=${mintAddress.slice(0, 8)}...` : `id=${pieceId}`;
      return {
        success: false,
        message: `Piece not found for provided ${mintAddress ? 'mint address' : 'id'}`,
        code: 'NOT_FOUND',
      };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error updating piece:', error);
    throw error;
  }
};

/**
 * Delete piece and its assets
 * @param {number|string|Object} identifier - Piece ID (number), mint address (string), or { pieceId, mintAddress }
 * @returns {Promise<boolean>} - True if deleted successfully
 */
export const deletePiece = async (identifier) => {
  try {
    console.log('🗑️ Starting piece deletion process...');
    
    // Normalize input
    let pieceId, mintAddress;
    
    if (typeof identifier === 'object' && identifier !== null) {
      pieceId = identifier.pieceId;
      mintAddress = identifier.mintAddress;
    } else {
      const numericId = asNumericId(identifier);
      if (numericId !== null) {
        pieceId = numericId;
      } else if (isMintAddress(identifier)) {
        mintAddress = identifier;
      } else {
        pieceId = identifier; // Fall back
      }
    }
    
    // First get the piece to access asset paths
    const pieceResult = await getPieceById(identifier);
    if (!pieceResult.success || !pieceResult.data) {
      const displayId = mintAddress ? `mint=${mintAddress.slice(0, 8)}...` : `id=${pieceId}`;
      console.log('❌ Piece not found:', pieceResult.message || displayId);
      return false;
    }
    
    const piece = pieceResult.data;

    // Delete assets from storage
    if (piece.asset_paths) {
      console.log('Deleting assets from storage...');
      await delete3DModelAssets(piece.asset_paths);
    }

    // Delete from database using dual-key filter
    let query = supabase
      .from(TABLES.PIECES)
      .delete();
    
    query = applyPieceFilter(query, { pieceId, mintAddress, isWrite: true });
    
    const { error } = await query;

    if (error) {
      console.error('❌ Error deleting piece from database:', error);
      throw error;
    }

    const displayId = mintAddress ? `mint=${mintAddress.slice(0, 8)}...` : `id=${pieceId}`;
    console.log('✅ Piece deleted successfully:', displayId);
    return true;
  } catch (error) {
    console.error('❌ Error in deletePiece:', error);
    throw error;
  }
};

/**
 * Create a new piece with 3D model storage
 * @param {Object} metadata - Piece metadata (name, category, description, etc.)
 * @param {File} glbFile - The 3D model file (GLB/GLTF)
 * @param {string} walletAddress - The user's wallet address
 * @param {File} thumbnailFile - Optional thumbnail image
 * @param {File} posterFile - Optional poster image
 * @returns {Promise<Object>} - The saved piece
 */
export const createPiece = async (metadata, glbFile, walletAddress, thumbnailFile = null, posterFile = null) => {
  try {
    console.log('🚀 Starting piece creation with 3D model...');
    console.log('Metadata:', metadata);
    console.log('GLB File:', glbFile?.name, glbFile?.size);
    console.log('Wallet:', walletAddress);
    
    // Validate inputs
    if (!walletAddress || typeof walletAddress !== 'string') {
      throw new Error('Valid wallet address is required');
    }
    
    if (!metadata || typeof metadata !== 'object') {
      throw new Error('Valid metadata is required');
    }
    
    // Get or create creator
    console.log('Getting or creating creator...');
    const creator = await getOrCreateCreator(walletAddress);
    console.log('Creator:', creator);

    // Handle both File objects and existing file objects (for drafts)
    const isExistingFile = glbFile && typeof glbFile === 'object' && glbFile.isExisting;
    const fileName = isExistingFile ? 'existing-model' : (glbFile?.name || 'untitled');
    
    // Upload 3D model assets to Supabase storage (only if not existing)
    console.log('Uploading 3D model assets...');
    let assetData;
    if (isExistingFile) {
      // For existing files, use the existing URLs
      const isExistingThumbnail = thumbnailFile && typeof thumbnailFile === 'object' && thumbnailFile.isExisting;
      assetData = {
        originalUrl: glbFile.url,
        previewUrl: glbFile.url,
        thumbnailUrl: isExistingThumbnail ? thumbnailFile.url : null,
        posterUrl: null,
        originalPath: null,
        previewPath: null,
        thumbnailPath: null,
        posterPath: null
      };
    } else {
      assetData = await upload3DModelAssets(glbFile, walletAddress, thumbnailFile, posterFile);
    }
    console.log('Asset upload completed:', assetData);

    // Prepare piece data for database
    const piecePrice = metadata.price || 0;
    
    // Validate and clamp royalty_bps (0-1000 = 0-10%)
    let royaltyBps = metadata.royaltyBps ?? 0; // Default 0%
    royaltyBps = Math.max(0, Math.min(1000, Math.round(royaltyBps))); // Clamp to 0-1000
    
    // Determine edition flags based on quantity
    const quantity = metadata.quantity || 1;
    const isMultiEdition = quantity > 1;
    
    const pieceData = {
      creator_id: creator.id,
      name: metadata.name || fileName.replace(/\.[^/.]+$/, ''),
      category: metadata.category || 'other',
      description: metadata.description || '',
      brand: metadata.brand || '',
      color: metadata.color || '',
      rarity: metadata.rarity || 'Common',
      price: piecePrice,
      quantity: quantity,
      total_made: metadata.totalMade || quantity,
      tags: metadata.tags || [],
      royalty_bps: royaltyBps,
      
      // Piece-specific social links (filled in during piece creation)
      social_links: metadata.socialLinks || {},
      
      // Edition-related fields for master pieces
      is_master: true,  // All created pieces are masters
      total_quantity: quantity,
      available_count: 0,  // Will be updated when editions are minted/listed
      sold_count: 0,
      edition_number: isMultiEdition ? null : 1,  // 1/1 pieces are edition 1, multi-edition masters have no edition_number
      master_id: null,  // This is a master, not an edition
      
      // IMPORTANT: All pieces MUST be created as drafts initially
      // They will only become non-draft after successful minting (when mint_address is set)
      // This is enforced by a database constraint: pieces_must_be_draft_or_minted
      is_draft: true,  // Always true at creation - changed to false only after minting
      is_public: false,  // Drafts are not public - changed to true after minting
      
      // Listing status - initially draft, updated to 'listed' after minting
      listing_status: 'draft',
      listing_price: piecePrice,
      listed_at: null,  // Set when piece is actually minted and listed
      
      creator_wallet: walletAddress,
      
      // Set creator as initial holder (they own the piece until it's sold)
      holder_wallet: walletAddress,
      
      // Asset URLs (using the existing database schema)
      thumbnail_url: assetData.thumbnailUrl || null,
      poster_url: assetData.posterUrl || null,
      preview_glb_url: assetData.previewUrl || null,
      model_url: assetData.originalUrl || null,
      
      // Asset paths for deletion
      asset_paths: {
        originalPath: assetData.originalPath,
        previewPath: assetData.previewPath,
        thumbnailPath: assetData.thumbnailPath,
        posterPath: assetData.posterPath
      },
      
      // File info
      file_name: fileName,
      file_size: isExistingFile ? 0 : (glbFile?.size || 0),
      file_type: isExistingFile ? 'model/gltf-binary' : (glbFile?.type || 'model/gltf-binary')
    };
    
    // Save to database
    const savedPiece = await savePieceToDatabase(pieceData);
    
    // Fetch the piece again with creator information
    console.log('Fetching piece with creator info...');
    const pieceResult = await getPieceById(savedPiece.id);
    
    if (!pieceResult.success || !pieceResult.data) {
      console.warn('⚠️ Could not refetch piece with creator info');
      return savedPiece; // Return original saved piece
    }
    
    console.log('✅ Piece creation completed:', pieceResult.data);
    return pieceResult.data;
  } catch (error) {
    console.error('❌ Error in piece creation process:', error);
    throw error;
  }
};

/**
 * Create a new capsule with multiple pieces
 * @param {Object} capsuleData - Capsule metadata (name, description, etc.)
 * @param {Array} pieces - Array of pieces with 3D models and thumbnails
 * @param {string} walletAddress - The user's wallet address
 * @returns {Promise<Object>} - The saved capsule with pieces
 */
export const createCapsule = async (capsuleData, pieces, walletAddress, bannerFile = null) => {
  try {
    console.log('🚀 Starting capsule creation...');
    console.log('Capsule data:', capsuleData);
    console.log('Pieces:', pieces.length);
    console.log('Wallet:', walletAddress);
    
    // Validate inputs
    if (!walletAddress || typeof walletAddress !== 'string') {
      throw new Error('Valid wallet address is required');
    }
    
    if (!capsuleData || typeof capsuleData !== 'object') {
      throw new Error('Valid capsule data is required');
    }
    
    if (!pieces || !Array.isArray(pieces) || pieces.length === 0) {
      throw new Error('At least one piece is required for a capsule');
    }
    
    // Get or create creator
    console.log('Getting or creating creator...');
    const creator = await getOrCreateCreator(walletAddress);
    console.log('Creator:', creator);
    
    // Create pieces in Supabase with batch processing for efficiency
    const createdPieces = [];
    const BATCH_SIZE = 5; // Process 5 pieces concurrently for optimal performance
    
    console.log(`🚀 Processing ${pieces.length} pieces in batches of ${BATCH_SIZE}...`);
    
    for (let batchStart = 0; batchStart < pieces.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, pieces.length);
      const batch = pieces.slice(batchStart, batchEnd);
      
      console.log(`📦 Processing batch ${Math.floor(batchStart / BATCH_SIZE) + 1}/${Math.ceil(pieces.length / BATCH_SIZE)} (pieces ${batchStart + 1}-${batchEnd})`);
      
      // Process batch concurrently
      const batchPromises = batch.map(async (piece, batchIndex) => {
        const globalIndex = batchStart + batchIndex;
        console.log(`Creating piece ${globalIndex + 1}/${pieces.length}: ${piece.modelName || piece.name}`);
        
        try {
          const pieceMetadata = {
            name: piece.modelName || piece.name,
            category: piece.modelCategory || piece.category || 'other',
            description: piece.modelDescription || piece.description || '',
            color: piece.modelColor || piece.color || '',
            price: parseFloat(piece.modelPrice || piece.price || 0),
            quantity: parseInt(piece.modelQuantity || piece.quantity || 1),
            royaltyBps: piece.modelRoyaltyBps || 0, // Default 0% if not provided
            tags: piece.modelTags || piece.tags || []
          };
          
          console.log(`🏷️ Creating piece ${globalIndex + 1} with tags:`, pieceMetadata.tags);
          
          // Create the piece using the existing createPiece function
          const createdPiece = await createPiece(
            pieceMetadata,
            piece.file, // The actual File object
            walletAddress,
            piece.thumbnailFile, // Thumbnail File object
            null // No poster file for now
          );
          
          console.log(`✅ Piece ${globalIndex + 1} created successfully`);
          return createdPiece;
          
        } catch (error) {
          console.error(`❌ Error creating piece ${globalIndex + 1}:`, error);
          return null; // Return null for failed pieces
        }
      });
      
      // Wait for batch to complete
      const batchResults = await Promise.all(batchPromises);
      
      // Add successful pieces to the list
      batchResults.forEach(result => {
        if (result) createdPieces.push(result);
      });
      
      console.log(`✅ Batch complete. ${createdPieces.length}/${batchEnd} pieces created so far.`);
    }
    
    if (createdPieces.length === 0) {
      throw new Error('Failed to create any pieces for the capsule');
    }
    
    // Upload banner if provided
    let bannerBucket = null;
    let bannerKey = null;
    
    if (bannerFile) {
      try {
        console.log('📸 Uploading banner...');
        const bannerFileName = `banner-${Date.now()}-${bannerFile.name}`;
        
        // Try capsule-banners bucket first, fallback to nft-previews
        let bucketName = 'capsule-banners';
        let { data: bannerUpload, error: bannerError } = await supabase.storage
          .from(bucketName)
          .upload(bannerFileName, bannerFile);
          
        // If capsule-banners bucket doesn't exist, try nft-previews bucket
        if (bannerError && bannerError.message?.includes('Bucket not found')) {
          console.log('📸 capsule-banners bucket not found, trying nft-previews...');
          bucketName = 'nft-previews';
          const retryResult = await supabase.storage
            .from(bucketName)
            .upload(bannerFileName, bannerFile);
          bannerUpload = retryResult.data;
          bannerError = retryResult.error;
        }
          
        if (bannerError) {
          console.error('❌ Banner upload failed:', bannerError);
          // Continue without banner rather than failing the entire capsule creation
        } else {
          bannerBucket = bucketName;
          bannerKey = bannerUpload.path;
          console.log('✅ Banner uploaded successfully to', bucketName, ':', bannerUpload);
        }
      } catch (error) {
        console.error('❌ Error uploading banner:', error);
        // Continue without banner
      }
    }

    // Create capsule record in database
    const capsuleRecord = {
      creator_id: creator.id,
      name: capsuleData.name,
      description: capsuleData.description || '',
      website: capsuleData.website || '',
      twitter: capsuleData.twitter || '',
      discord: capsuleData.discord || '',
      instagram: capsuleData.instagram || '',
      banner_bucket: bannerBucket,
      banner_key: bannerKey,
      piece_count: createdPieces.length,
      status: 'active',
      dropped_at: new Date().toISOString() // Set the drop date to now
    };
    
    // Save capsule to database
    const { data: savedCapsule, error: capsuleError } = await supabase
      .from('capsules')
      .insert(capsuleRecord)
      .select()
      .maybeSingle();
    
    if (capsuleError) {
      throw new Error(`Failed to save capsule: ${capsuleError.message}`);
    }
    
    // Link pieces to capsule
    const pieceIds = createdPieces.map(piece => piece.id);
    const { error: linkError } = await supabase
      .from('capsule_pieces')
      .insert(
        pieceIds.map(pieceId => ({
          capsule_id: savedCapsule.id,
          piece_id: pieceId
        }))
      );
    
    if (linkError) {
      console.error('Warning: Failed to link pieces to capsule:', linkError);
    }
    
    console.log('✅ Capsule creation completed:', savedCapsule);
    return {
      ...savedCapsule,
      pieces: createdPieces
    };
    
  } catch (error) {
    console.error('❌ Error in capsule creation process:', error);
    throw error;
  }
};

/**
 * Get all capsules with pagination
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of capsules per page
 * @returns {Promise<Object>} - Object with capsules and pagination info
 */
export const getAllCapsules = async (page = 0, limit = 20) => {
  try {
    console.log('🔍 getAllCapsules: Starting query with page:', page, 'limit:', limit);
    const from = page * limit;
    const to = from + limit - 1;

    console.log('🔍 getAllCapsules: Query range from:', from, 'to:', to);

    const { data, error, count } = await supabase
      .from(TABLES.CAPSULES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .order('created_at', { ascending: false })
      .range(from, to);

    console.log('🔍 getAllCapsules: Supabase response - data:', data, 'error:', error, 'count:', count);

    if (error) {
      console.error('❌ getAllCapsules: Error getting all capsules:', error);
      throw error;
    }

    // Enhance capsules with piece pricing data
    const enhancedCapsules = await Promise.all((data || []).map(async (capsule) => {
      try {
        // Get pieces for this capsule to calculate average price
        const { data: capsulePieces, error: piecesError } = await supabase
          .from(TABLES.CAPSULE_PIECES)
          .select(`
            pieces (
              id,
              price,
              listing_price
            )
          `)
          .eq('capsule_id', capsule.id);

        if (piecesError) {
          console.error('❌ Error getting pieces for capsule:', capsule.id, piecesError);
          return capsule;
        }

        const pieces = capsulePieces?.map(cp => cp.pieces).filter(Boolean) || [];
        
        console.log('🔢 getAllCapsules: Capsule', capsule.name, '- pieces found:', pieces.length, 'db piece_count:', capsule.piece_count);
        console.log('💰 Piece prices:', pieces.map(p => ({ id: p.id, price: p.price, listing_price: p.listing_price })));
        
        // Calculate average price - use listing_price if available, otherwise use price
        const totalPrice = pieces.reduce((sum, piece) => {
          const priceToUse = parseFloat(piece.listing_price || piece.price) || 0;
          return sum + priceToUse;
        }, 0);
        const avgPrice = pieces.length > 0 ? totalPrice / pieces.length : 0;
        
        if (avgPrice === 0 && pieces.length > 0) {
          console.warn('⚠️ Capsule has 0 average price - pieces may need prices set:', capsule.name);
        }
        
        console.log('📊 Average price calculation:', { capsule: capsule.name, totalPrice, pieceCount: pieces.length, avgPrice });

        // Get banner URL - check banner_url first (new system), then fall back to banner_bucket/banner_key (old system)
        let bannerUrl = capsule.banner_url || null;
        if (!bannerUrl && capsule.banner_bucket && capsule.banner_key) {
          const { data: bannerData } = await supabase.storage
            .from(capsule.banner_bucket)
            .getPublicUrl(capsule.banner_key);
          bannerUrl = bannerData?.publicUrl;
        }

        return {
          ...capsule,
          avgPrice: avgPrice.toFixed(2),
          banner: bannerUrl,
          pieceCount: pieces.length || capsule.piece_count || 0,
          // Ensure we have the right field names for the UI
          title: capsule.name,
          name: capsule.name,
          image: bannerUrl,
          // Date fields for UI
          dropDate: capsule.dropped_at || capsule.created_at,
          createdAt: capsule.created_at
        };
      } catch (error) {
        console.error('❌ Error enhancing capsule:', capsule.id, error);
        return capsule;
      }
    }));

    const result = {
      capsules: enhancedCapsules,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };

    console.log('🔍 getAllCapsules: Returning enhanced result:', result);
    return result;
  } catch (error) {
    console.error('❌ getAllCapsules: Error in getAllCapsules:', error);
    throw error;
  }
};

/**
 * Get trending capsules
 * @param {number} limit - Number of capsules to return
 * @returns {Promise<Array>} - Array of trending capsules
 */
export const getTrendingCapsules = async (limit = 20) => {
  try {
    console.log('🔥 getTrendingCapsules: Getting trending capsules, limit:', limit);

    // Query the trending_capsules view
    const { data, error } = await supabase
      .from('trending_capsules')
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `)
      .limit(limit);

    if (error) {
      console.error('❌ Error getting trending capsules:', error);
      // Fallback to regular getAllCapsules if view doesn't exist
      console.log('⚠️ Falling back to getAllCapsules');
      const fallback = await getAllCapsules(0, limit);
      return fallback.capsules || [];
    }

    if (!data || data.length === 0) {
      console.log('⚠️ No trending capsules found, falling back to getAllCapsules');
      const fallback = await getAllCapsules(0, limit);
      return fallback.capsules || [];
    }

    // Enhance capsules with piece pricing data (same as getAllCapsules)
    const enhancedCapsules = await Promise.all((data || []).map(async (capsule) => {
      try {
        // Get pieces for this capsule to calculate average price
        const { data: capsulePieces, error: piecesError } = await supabase
          .from(TABLES.CAPSULE_PIECES)
          .select(`
            pieces (
              id,
              price,
              listing_price
            )
          `)
          .eq('capsule_id', capsule.id);

        if (piecesError) {
          console.error('❌ Error getting pieces for capsule:', capsule.id, piecesError);
          return capsule;
        }

        const pieces = capsulePieces?.map(cp => cp.pieces).filter(Boolean) || [];
        
        console.log('🔥 getTrendingCapsules: Capsule', capsule.name, '- pieces found:', pieces.length);
        console.log('💰 Piece prices:', pieces.map(p => ({ id: p.id, price: p.price, listing_price: p.listing_price })));
        
        // Calculate average price - use listing_price if available, otherwise use price
        const totalPrice = pieces.reduce((sum, piece) => {
          const priceToUse = parseFloat(piece.listing_price || piece.price) || 0;
          return sum + priceToUse;
        }, 0);
        const avgPrice = pieces.length > 0 ? totalPrice / pieces.length : 0;
        
        if (avgPrice === 0 && pieces.length > 0) {
          console.warn('⚠️ Capsule has 0 average price - pieces may need prices set:', capsule.name);
        }
        
        console.log('📊 Average price calculation:', { capsule: capsule.name, totalPrice, pieceCount: pieces.length, avgPrice });

        // Get banner URL - check banner_url first (new system), then fall back to banner_bucket/banner_key (old system)
        let bannerUrl = capsule.banner_url || null;
        if (!bannerUrl && capsule.banner_bucket && capsule.banner_key) {
          const { data: bannerData } = await supabase.storage
            .from(capsule.banner_bucket)
            .getPublicUrl(capsule.banner_key);
          bannerUrl = bannerData?.publicUrl;
        }

        return {
          ...capsule,
          avgPrice: avgPrice.toFixed(2),
          banner: bannerUrl,
          pieceCount: pieces.length || capsule.piece_count || 0,
          // Ensure we have the right field names for the UI
          title: capsule.name,
          name: capsule.name,
          image: bannerUrl,
          // Date fields for UI
          dropDate: capsule.dropped_at || capsule.created_at,
          createdAt: capsule.created_at
        };
      } catch (error) {
        console.error('❌ Error enhancing capsule:', capsule.id, error);
        return capsule;
      }
    }));

    console.log('✅ Found', enhancedCapsules.length, 'trending capsules');
    return enhancedCapsules;

  } catch (error) {
    console.error('❌ Error in getTrendingCapsules:', error);
    // Final fallback
    const fallback = await getAllCapsules(0, limit);
    return fallback.capsules || [];
  }
};

/**
 * Get capsule by ID with all its pieces
 * @param {string} capsuleId - Capsule ID
 * @returns {Promise<Object|null>} - The capsule with pieces or null if not found
 */
export const getCapsuleById = async (capsuleId) => {
  try {
    console.log('🔍 getCapsuleById: Getting capsule:', capsuleId);

    // Get the capsule
    const { data: capsule, error: capsuleError } = await supabase
      .from(TABLES.CAPSULES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `)
      .eq('id', capsuleId)
      .maybeSingle();

    if (capsuleError) {
      console.error('❌ getCapsuleById: Error getting capsule:', capsuleError);
      if (capsuleError.code === 'PGRST116') {
        return null; // Not found
      }
      throw capsuleError;
    }

    // Get the pieces for this capsule
    const { data: capsulePieces, error: piecesError } = await supabase
      .from(TABLES.CAPSULE_PIECES)
      .select(`
        pieces (
          *,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            verification_type,
            profile_picture_url,
            social_links,
            total_pieces_created
          )
        )
      `)
      .eq('capsule_id', capsuleId);

    if (piecesError) {
      console.error('❌ getCapsuleById: Error getting capsule pieces:', piecesError);
      throw piecesError;
    }

    // Extract the pieces from the capsule_pieces relationship
    // NOTE: capsule_pieces now contains ALL edition pieces (not just master pieces)
    // after our fix to CreateCollection.jsx, so we don't need to fetch additional editions
    let pieces = capsulePieces?.map(cp => cp.pieces).filter(Boolean) || [];

    console.log('✅ getCapsuleById: Found capsule with', pieces.length, 'pieces (including all editions from capsule_pieces)');
    console.log('✅ getCapsuleById: First piece raw data:', pieces[0]);

    // Get banner URL - check banner_url first (new system), then fall back to banner_bucket/banner_key (old system)
    let bannerUrl = capsule.banner_url || null;
    if (!bannerUrl && capsule.banner_bucket && capsule.banner_key) {
      const { data: bannerData } = await supabase.storage
        .from(capsule.banner_bucket)
        .getPublicUrl(capsule.banner_key);
      bannerUrl = bannerData?.publicUrl;
    }

    // Convert thumbnail URLs to public URLs for each piece
    pieces = pieces.map(piece => {
      let thumbnailUrl = piece.thumbnail_url;
      let previewGlbUrl = piece.preview_glb_url;
      let modelUrl = piece.model_url;
      let posterUrl = piece.poster_url;

      // If thumbnail_url exists and doesn't already start with http, convert it to public URL
      if (thumbnailUrl && !thumbnailUrl.startsWith('http')) {
        const { data: thumbData } = supabase.storage
          .from(STORAGE_BUCKETS.PREVIEWS)
          .getPublicUrl(thumbnailUrl);
        thumbnailUrl = thumbData?.publicUrl || thumbnailUrl;
      }

      // Same for preview GLB URL
      if (previewGlbUrl && !previewGlbUrl.startsWith('http')) {
        const { data: previewData } = supabase.storage
          .from(STORAGE_BUCKETS.PREVIEWS)
          .getPublicUrl(previewGlbUrl);
        previewGlbUrl = previewData?.publicUrl || previewGlbUrl;
      }

      // Same for model URL
      if (modelUrl && !modelUrl.startsWith('http')) {
        const { data: modelData } = supabase.storage
          .from(STORAGE_BUCKETS.ORIGINALS)
          .getPublicUrl(modelUrl);
        modelUrl = modelData?.publicUrl || modelUrl;
      }

      // Same for poster URL
      if (posterUrl && !posterUrl.startsWith('http')) {
        const { data: posterData } = supabase.storage
          .from(STORAGE_BUCKETS.PREVIEWS)
          .getPublicUrl(posterUrl);
        posterUrl = posterData?.publicUrl || posterUrl;
      }

      return {
        ...piece,
        thumbnail_url: thumbnailUrl,
        preview_glb_url: previewGlbUrl,
        model_url: modelUrl,
        poster_url: posterUrl
      };
    });

    console.log('✅ getCapsuleById: First piece with converted URLs:', pieces[0]);

    return {
      ...capsule,
      pieces: pieces,
      banner: bannerUrl, // Already retrieved above
      collectionModels: pieces // Add this alias for compatibility
    };
  } catch (error) {
    console.error('❌ getCapsuleById: Error in getCapsuleById:', error);
    throw error;
  }
};

/**
 * Get capsule information for a specific piece
 * @param {number} pieceId - Piece ID
 * @returns {Promise<Object|null>} - The capsule information or null if piece is not in a capsule
 */
export const getCapsuleForPiece = async (pieceId) => {
  try {
    console.log('🔍 getCapsuleForPiece: Getting capsule for piece:', pieceId);

    // Get the capsule information for this piece
    const { data: capsulePiece, error } = await supabase
      .from(TABLES.CAPSULE_PIECES)
      .select(`
        capsule_id,
        capsules (
          id,
          name,
          description,
          banner_bucket,
          banner_key,
          status,
          website,
          twitter,
          discord,
          instagram,
          creators (
            id,
            wallet,
            handle,
            is_verified,
            total_pieces_created
          )
        )
      `)
      .eq('piece_id', pieceId)
      .eq('capsules.status', 'active')
      .maybeSingle();

    console.log('🔍 getCapsuleForPiece: Query result:', { capsulePiece, error });

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('✅ getCapsuleForPiece: Piece is not part of any capsule (PGRST116)');
        return null; // Not found - piece is not in a capsule
      }
      console.error('❌ getCapsuleForPiece: Error getting capsule for piece:', error);
      throw error;
    }

    if (!capsulePiece?.capsules) {
      console.log('✅ getCapsuleForPiece: Piece is not part of any capsule (no capsules data)');
      return null;
    }

    const capsule = capsulePiece.capsules;

    // Get banner URL - check banner_url first (new system), then fall back to banner_bucket/banner_key (old system)
    let bannerUrl = capsule.banner_url || null;
    if (!bannerUrl && capsule.banner_bucket && capsule.banner_key) {
      const { data: bannerData } = await supabase.storage
        .from(capsule.banner_bucket)
        .getPublicUrl(capsule.banner_key);
      bannerUrl = bannerData?.publicUrl;
    }

    console.log('✅ getCapsuleForPiece: Found capsule for piece:', capsule.name);

    return {
      ...capsule,
      banner: bannerUrl
    };
  } catch (error) {
    console.error('❌ getCapsuleForPiece: Error in getCapsuleForPiece:', error);
    throw error;
  }
};

/**
 * Search pieces by query
 * @param {string} query - Search query
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} - Object with pieces and pagination info
 */
export const searchPieces = async (query, page = 0, limit = 20) => {
  try {
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .or(`name.ilike.%${query}%,description.ilike.%${query}%,brand.ilike.%${query}%`)
      .eq('listing_status', 'listed')  // Only listed pieces
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error searching pieces:', error);
      throw error;
    }

    // Add holder information to pieces
    const piecesWithHolders = await Promise.all((data || []).map(async (piece) => {
      if (piece.holder_wallet) {
        try {
          const { data: holderData } = await supabase
            .from('creators')
            .select('id, wallet, handle, is_verified, verification_type, profile_picture_url, banner_url, bio, social_links, total_pieces_created')
            .eq('wallet', piece.holder_wallet)
            .maybeSingle();
          
          return {
            ...piece,
            holder: holderData
          };
        } catch (error) {
          console.log('No holder profile found for wallet:', piece.holder_wallet);
          return piece;
        }
      }
      return piece;
    }));

    return {
      pieces: piecesWithHolders,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Error in searchPieces:', error);
    throw error;
  }
};

/**
 * Get pieces by creator (public pieces only)
 * @param {string} walletAddress - Creator's wallet address
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} - Object with pieces and pagination info
 */
export const getPiecesByCreator = async (walletAddress, page = 0, limit = 20) => {
  try {
    console.log('🔍 getPiecesByCreator: Fetching public pieces for wallet:', walletAddress);
    
    // First, get the creator ID from the wallet
    const { data: creator, error: creatorError } = await supabase
      .from(TABLES.CREATORS)
      .select('id')
      .eq('wallet', walletAddress)
      .maybeSingle();

    if (creatorError || !creator) {
      console.log('⚠️ getPiecesByCreator: Creator not found for wallet:', walletAddress);
      return {
        pieces: [],
        total: 0,
        page,
        limit,
        totalPages: 0
      };
    }

    console.log('✅ getPiecesByCreator: Found creator ID:', creator.id);

    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('creator_id', creator.id)
      .eq('listing_status', 'listed')  // Only listed pieces
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error getting pieces by creator:', error);
      throw error;
    }

    console.log('✅ getPiecesByCreator: Found public pieces:', data?.length || 0);
    console.log('✅ getPiecesByCreator: Sample piece:', data?.[0]);

    return {
      pieces: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Error in getPiecesByCreator:', error);
    throw error;
  }
};

/**
 * Get ALL pieces created by a specific wallet address (including drafts, public, private, etc.)
 * This is used for the "My Creations" page where users see everything they've created
 * @param {string} walletAddress - Creator's wallet address
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} - Object with pieces and pagination info
 */
export const getAllPiecesByCreator = async (walletAddress, page = 0, limit = 100) => {
  try {
    console.log('🔍 getAllPiecesByCreator: Fetching ALL pieces for wallet:', walletAddress);
    
    const from = page * limit;
    const to = from + limit - 1;

    // IMPORTANT FIX: Filter by creator_wallet directly, not by creator_id
    // This ensures we only get pieces actually created by this wallet address
    // Some test pieces might have incorrect creator_id mappings
    const { data, error, count } = await supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'exact' })
      .eq('creator_wallet', walletAddress)  // Filter by wallet address directly
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('❌ Error getting all pieces by creator:', error);
      throw error;
    }

    console.log('✅ getAllPiecesByCreator: Found', data?.length || 0, 'pieces for wallet:', walletAddress);
    console.log('📊 Pieces:', data?.map(p => ({ id: p.id, name: p.name, creator_wallet: p.creator_wallet })));

    return {
      pieces: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Error in getAllPiecesByCreator:', error);
    throw error;
  }
};

/**
 * Get pieces owned by a specific wallet address
 * @param {string} walletAddress - Holder's wallet address
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} - Object with owned pieces and pagination info
 */
export const getPiecesByHolder = async (walletAddress, page = 0, limit = 20) => {
  try {
    console.log('🔍 getPiecesByHolder: Fetching pieces owned by wallet:', walletAddress);
    
    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('holder_wallet', walletAddress)
      .not('mint_address', 'is', null)  // Only minted pieces (pieces that exist on blockchain)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error getting pieces by holder:', error);
      throw error;
    }

    console.log('✅ getPiecesByHolder: Found owned pieces:', data?.length || 0);
    console.log('✅ getPiecesByHolder: Sample piece:', data?.[0]);

    return {
      pieces: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Error in getPiecesByHolder:', error);
    throw error;
  }
};

/**
 * Get user's drafts (private pieces)
 * @param {string} walletAddress - Creator's wallet address
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of pieces per page
 * @returns {Promise<Object>} - Object with draft pieces and pagination info
 */
export const getUserDrafts = async (walletAddress, page = 0, limit = 20) => {
  try {
    console.log('🔍 getUserDrafts: Fetching drafts for wallet:', walletAddress);
    
    // First, get the creator ID from the wallet
    const { data: creator, error: creatorError } = await supabase
      .from(TABLES.CREATORS)
      .select('id')
      .eq('wallet', walletAddress)
      .maybeSingle();

    if (creatorError || !creator) {
      console.log('⚠️ getUserDrafts: Creator not found for wallet:', walletAddress);
      return {
        pieces: [],
        total: 0,
        page,
        limit,
        totalPages: 0
      };
    }

    console.log('✅ getUserDrafts: Found creator ID:', creator.id);

    const from = page * limit;
    const to = from + limit - 1;

    const { data, error, count } = await supabase
      .from(TABLES.PIECES)
      .select(`
        *,
        social_links,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          banner_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('creator_id', creator.id)
      .eq('listing_status', 'draft')  // Only draft pieces
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Error getting user drafts:', error);
      throw error;
    }

    console.log('✅ getUserDrafts: Found draft pieces:', data?.length || 0);
    console.log('✅ getUserDrafts: Sample draft:', data?.[0]);

    return {
      pieces: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Error in getUserDrafts:', error);
    throw error;
  }
};

/**
 * Get piece statistics
 * @returns {Promise<Object>} - Piece statistics
 */
export const getPieceStatistics = async () => {
  try {
    const { data, error } = await supabase
      .from(TABLES.PIECES)
      .select('category, rarity, file_size');

    if (error) {
      console.error('Error getting piece statistics:', error);
      throw error;
    }

    const total = data.length;
    const byCategory = {};
    const byRarity = {};
    let totalSize = 0;

    data.forEach(piece => {
      // Count by category
      byCategory[piece.category] = (byCategory[piece.category] || 0) + 1;
      
      // Count by rarity
      byRarity[piece.rarity] = (byRarity[piece.rarity] || 0) + 1;
      
      // Sum file sizes
      totalSize += piece.file_size || 0;
    });

    return {
      total,
      byCategory,
      byRarity,
      totalSize
    };
  } catch (error) {
    console.error('Error in getPieceStatistics:', error);
    throw error;
  }
};

/**
 * Update piece listing status and price
 * @param {Object|number|string} pieceIdentifier - Piece ID (number), mint address (string), or { pieceId, mintAddress }
 * @param {string} status - New listing status ('draft', 'listed', 'sold', 'expired', 'cancelled')
 * @param {number} listingPrice - New listing price (optional)
 * @returns {Promise<Object>} - Updated piece
 */
export const updatePieceListingStatus = async (pieceIdentifier, status, listingPrice = null) => {
  try {
    // Normalize input: support both old (single arg) and new (object) signatures
    let pieceId, mintAddress;
    
    if (typeof pieceIdentifier === 'object' && pieceIdentifier !== null) {
      pieceId = pieceIdentifier.pieceId;
      mintAddress = pieceIdentifier.mintAddress;
    } else {
      // Legacy: single arg that might be numeric ID or mint address
      const numericId = asNumericId(pieceIdentifier);
      if (numericId !== null) {
        pieceId = numericId;
      } else if (isMintAddress(pieceIdentifier)) {
        mintAddress = pieceIdentifier;
      } else {
        pieceId = pieceIdentifier; // Fall back to original value
      }
    }
    
    const displayId = mintAddress ? `mint=${mintAddress.slice(0, 8)}...` : `id=${pieceId}`;
    console.log(`🔄 Updating piece ${displayId} listing status to ${status}`);
    
    const updateData = {
      listing_status: status,
      updated_at: new Date().toISOString()
    };
    
    // Update listing price if provided
    if (listingPrice !== null) {
      updateData.listing_price = listingPrice;
      updateData.price = listingPrice;
    }
    
    // Set listed_at timestamp when listing
    if (status === 'listed' && listingPrice > 0) {
      updateData.listed_at = new Date().toISOString();
    }
    
    // Set sold_at timestamp when sold
    if (status === 'sold') {
      updateData.sold_at = new Date().toISOString();
    }
    
    // Apply dual-key filter to avoid 22P02 error (write operation)
    let query = supabase
      .from('pieces')
      .update(updateData);
    
    query = applyPieceFilter(query, { pieceId, mintAddress, isWrite: true });
    
    const { data, error } = await query
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('❌ Error updating piece listing status:', error);
      return {
        success: false,
        message: `Failed to update piece: ${error.message}`,
        code: error.code || 'UPDATE_ERROR',
      };
    }
    
    if (!data) {
      const displayId = mintAddress ? `mint=${mintAddress.slice(0, 8)}...` : `id=${pieceId}`;
      console.warn('⚠️ Piece not found for update:', displayId);
      // Special code for missing pieces that should be imported first
      const errorCode = mintAddress ? 'MINT_NOT_IMPORTED' : 'NOT_FOUND';
      return {
        success: false,
        message: `Piece not found for provided ${mintAddress ? 'mint address' : 'id'}. ${mintAddress ? 'Use create-listing edge function to import external NFTs.' : ''}`,
        code: errorCode,
      };
    }
    
    console.log('✅ Piece listing status updated successfully:', data?.name || displayId);
    
    // Log listing activity if piece was just listed
    if (status === 'listed' && data.mint_address && listingPrice > 0) {
      try {
        const now = new Date();
        const timestamp = Math.floor(now.getTime() / 1000);
        
        await supabase.from('nft_activity').insert({
          mint_address: data.mint_address,
          event_type: 'list',
          from_address: data.holder_wallet || data.creator_id,
          to_address: null,
          price: listingPrice,
          amount_lamports: Math.floor(listingPrice * 1000000000), // Convert SOL to lamports
          signature: 'app-list-' + Date.now(),
          tx_sig: 'app-list-' + Date.now(),
          block_time: now.toISOString(),
          created_at: now.toISOString(),
          event_timestamp: timestamp,
          source: 'app',
          slot: 0
        });
        
        console.log('✅ Listing activity logged for', data.mint_address);
      } catch (activityError) {
        console.warn('⚠️ Failed to log listing activity (non-critical):', activityError);
      }
    }
    
    return { success: true, data };
  } catch (error) {
    console.error('❌ Error in updatePieceListingStatus:', error);
    return {
      success: false,
      message: error.message || 'Failed to update piece listing status',
      code: 'EXCEPTION',
    };
  }
};

/**
 * Get user's listings (pieces they have listed for sale)
 * @param {string} walletAddress - User's wallet address
 * @param {string} status - Filter by listing status (optional)
 * @param {number} page - Page number (0-based)
 * @param {number} limit - Number of listings per page
 * @returns {Promise<Object>} - Object with listings and pagination info
 */
export const getUserListings = async (walletAddress, status = null, page = 0, limit = 50) => {
  try {
    console.log(`🔍 Getting listings for wallet: ${walletAddress}, status: ${status}, page: ${page}, limit: ${limit}`);
    
    // Get creator ID first
    const creator = await getOrCreateCreator(walletAddress);
    if (!creator) {
      throw new Error('Creator not found');
    }
    
    const from = page * limit;
    const to = from + limit - 1;
    
    // IMPORTANT: Filter by holder_wallet, not creator_id
    // The Listings page should only show pieces the user CURRENTLY OWNS
    // When someone buys your piece, you're still the creator but no longer the holder
    const { data, error, count } = await supabase
      .from('pieces')
      .select(`
        *,
        social_links,
        creators!inner(
          id,
          wallet,
          handle,
          is_verified,
          verification_type,
          profile_picture_url,
          bio,
          social_links,
          total_pieces_created
        )
      `, { count: 'planned' })
      .eq('holder_wallet', walletAddress)  // Only show pieces currently owned
      .eq('listing_status', 'listed')  // Only show actively listed pieces
      .order('listed_at', { ascending: false })
      .range(from, to);
    
    if (error) {
      console.error('❌ Error getting user listings:', error);
      throw error;
    }
    
    // STALE LISTING VALIDATION: Cross-check with listings table
    // Filter out pieces where the listing is inconsistent
    const validListings = [];
    
    for (const piece of data) {
      // Check if an active listing exists in the listings table
      const { data: activeListingCheck } = await supabase
        .from('listings')
        .select('id, seller_wallet, status')
        .eq('mint_address', piece.mint_address)
        .eq('status', 'active')
        .maybeSingle();
      
      // Only include if:
      // 1. An active listing exists in listings table, AND
      // 2. The listing's seller matches the piece's holder, AND
      // 3. The piece's listing_status is 'listed'
      if (
        activeListingCheck &&
        activeListingCheck.seller_wallet?.toLowerCase() === walletAddress.toLowerCase() &&
        piece.listing_status === 'listed'
      ) {
        validListings.push(piece);
      } else {
        console.warn('⚠️ Filtered out stale listing:', {
          mint: piece.mint_address,
          piece_holder: piece.holder_wallet,
          piece_status: piece.listing_status,
          listing_exists: !!activeListingCheck,
          listing_seller: activeListingCheck?.seller_wallet,
        });
        
        // Optionally heal this piece in the background (non-blocking)
        if (piece.listing_status === 'listed' && !activeListingCheck) {
          supabase.from('pieces')
            .update({
              listing_status: null,
              listing_price: null,
              is_delegate_active: false,
              listing_delegate: null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', piece.id)
            .then(() => console.log('✅ Auto-healed stale piece listing state'))
            .catch(err => console.warn('Failed to auto-heal:', err));
        }
      }
    }
    
    // Transform the data to match the listings page format
    const transformedListings = validListings.map(piece => ({
      ...piece,
      id: piece.id,
      name: piece.name,
      description: piece.description,
      category: piece.category,
      color: piece.color,
      price: piece.listing_price?.toString() || piece.price?.toString() || '0',
      originalPrice: piece.price?.toString() || '0',
      status: 'listed',
      listing_status: 'listed',
      views: piece.views || 0,
      likes: piece.likes || 0,
      offers: piece.offers || 0,
      listedAt: piece.listed_at,
      expiresAt: piece.expires_at,
      soldAt: piece.sold_at,
      createdDate: piece.created_at,
      quantity: piece.quantity || 1,
      thumbnailUrl: piece.thumbnail_url || '/images/piece-placeholder.png',
      thumbnail: {
        dataUrl: piece.thumbnail_url || '/images/piece-placeholder.png'
      },
      modelFile: {
        dataUrl: piece.preview_glb_url || piece.model_url,
        name: piece.file_name,
        type: piece.file_type
      },
      creator: {
        name: piece.creators?.handle || 'Unknown',
        username: piece.creators?.handle || 'unknown',
        piecesCount: piece.creators?.total_pieces_created || 0,
        wallet: piece.creators?.wallet,
        verified: piece.creators?.is_verified || false,
        profilePicture: piece.creators?.profile_picture_url
      },
      creator_wallet: piece.creator_wallet,
      mint_address: piece.mint_address,
      holder_wallet: piece.holder_wallet
    }));
    
    console.log(`✅ Found ${transformedListings.length} listings for user (page ${page}, total: ${count})`);
    console.log('📊 Sample listing with creator data:', transformedListings[0]);
    return {
      listings: transformedListings,
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('❌ Error in getUserListings:', error);
    throw error;
  }
};

export default {
  getOrCreateCreator,
  savePieceToDatabase,
  getPieceById,
  getAllPieces,
  getPiecesByCategory,
  updatePiece,
  deletePiece,
  createPiece,
  searchPieces,
  getPiecesByCreator,
  getPieceStatistics,
  updatePieceListingStatus,
  getUserListings
};
